/**
 * Resilient wrapper around React.lazy for code-split chunks.
 *
 * A plain `React.lazy(() => import(...))` has no retry: if the on-demand chunk
 * fetch fails — the file 404s because its hash went stale after a deploy, or a
 * transient network/dev-server hiccup drops the request — the rejection bubbles
 * to the nearest error boundary as a raw
 * "Failed to fetch dynamically imported module …" message and the feature looks
 * broken until the user manually reloads.
 *
 * `lazyWithRetry` retries the import once after a short delay, and if it still
 * fails, forces a single hard reload to pull a fresh index.html (and therefore
 * valid chunk hashes). A sessionStorage guard ensures we reload at most once per
 * failed chunk so a genuinely-missing chunk can't trap the user in a reload loop
 * — after the one reload the error is allowed to surface normally.
 */
import { lazy, type ComponentType } from 'react';

/** True for the errors a browser throws when a dynamic import can't be fetched. */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    // Vite dev: stale optimized-dep response
    /Outdated Optimize Dep/i.test(message)
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own
// `ComponentType<any>` constraint so components with required props keep their prop types.
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  /** Stable key identifying this chunk, used to gate the one-time reload. */
  chunkKey: string,
) {
  const reloadFlag = `lazyReload:${chunkKey}`;

  return lazy(async () => {
    try {
      const mod = await factory();
      // Success — clear any prior reload guard so a future stale deploy can
      // trigger a fresh reload.
      window.sessionStorage.removeItem(reloadFlag);
      return mod;
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      // Second attempt — covers a one-off network blip without a full reload.
      try {
        const mod = await new Promise<{ default: T }>((resolve, reject) => {
          setTimeout(() => factory().then(resolve, reject), 400);
        });
        window.sessionStorage.removeItem(reloadFlag);
        return mod;
      } catch (retryError) {
        if (!isChunkLoadError(retryError)) throw retryError;

        // Still failing — most likely a stale chunk hash after a deploy.
        // Hard-reload once to fetch a current index.html + chunk map.
        if (!window.sessionStorage.getItem(reloadFlag)) {
          window.sessionStorage.setItem(reloadFlag, '1');
          window.location.reload();
          // Return a never-resolving promise so Suspense keeps the fallback up
          // while the reload happens instead of flashing the error boundary.
          return new Promise<{ default: T }>(() => {});
        }
        // Already reloaded once and it's still broken — let the error surface.
        throw retryError;
      }
    }
  });
}
